import React, { useState } from 'react';
import { ArrowLeftIcon, EyeOffIcon, LayoutGridIcon, LayoutTemplateIcon, RotateCcwIcon, SlidersHorizontalIcon, ZapIcon } from 'lucide-react';
import { FormConfig, FormData } from '../types';
import { LayoutTab } from '../components/admin/LayoutTab';
import { RulesTab } from '../components/admin/RulesTab';
import { SectionsTab } from '../components/admin/SectionsTab';
type TabKey = 'sections' | 'rules' | 'layout';
const TABS: {
  key: TabKey;
  label: string;
  blurb: string;
  icon: React.ReactNode;
}[] = [{
  key: 'sections',
  label: 'Sections & Fields',
  blurb: 'Order, group, and hide',
  icon: <LayoutGridIcon className="h-4 w-4" data-id="element-351" data-mp="6iu6w2hthxlg" />
}, {
  key: 'rules',
  label: 'Visibility Rules',
  blurb: 'Conditional logic',
  icon: <ZapIcon className="h-4 w-4" data-id="element-352" data-mp="2r9ktaa0bvnr4" />
}, {
  key: 'layout',
  label: 'Layout & Display',
  blurb: 'Density and header',
  icon: <LayoutTemplateIcon className="h-4 w-4" data-id="element-353" data-mp="2vbpmj7mv6id8" />
}];
interface AdminConfigProps {
  config: FormConfig;
  values: FormData;
  onChange: (config: FormConfig) => void;
  onExit: () => void;
  onReset: () => void;
}
export function AdminConfig({
  config,
  values,
  onChange,
  onExit,
  onReset
}: AdminConfigProps) {
  const [tab, setTab] = useState<TabKey>('sections');
  const activeRules = config.rules.filter(r => r.enabled).length;
  const hiddenFields = config.hiddenFieldIds.length;
  const visibleSections = config.sections.filter(s => !s.hidden).length;
  const stats = [{
    label: 'Visible sections',
    value: `${visibleSections} of ${config.sections.length}`
  }, {
    label: 'Hidden fields',
    value: String(hiddenFields),
    icon: <EyeOffIcon className="h-3.5 w-3.5" data-id="element-354" data-mp="2i5lcxzw5loq8" />
  }, {
    label: 'Active rules',
    value: String(activeRules)
  }];
  return <div className="min-h-full w-full bg-slate-100" data-id="element-355" data-mp="2r46x1zh9xbt4">
      <header className="sticky top-0 z-30 border-b border-slate-200 bg-white" data-id="element-356" data-mp="2r4qbv7bmnk2x">
        <div className="mx-auto flex max-w-7xl items-center gap-3 px-4 py-3 sm:px-6" data-id="element-357" data-mp="2m65b96sgqydy">
          <button type="button" onClick={onExit} className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 px-3 py-2 text-sm font-semibold text-slate-600 transition-colors hover:bg-slate-50 hover:text-slate-900" data-id="element-358" data-mp="1jsh9ey7akowv">
            <ArrowLeftIcon className="h-4 w-4" data-id="element-359" data-mp="1xiui52s0c7n9" />
            Back to record
          </button>

          <span className="hidden h-6 w-px bg-slate-200 sm:block" data-id="element-360" data-mp="22ifq22p5nqc4" />

          <span className="hidden h-9 w-9 items-center justify-center rounded-lg bg-slate-900 text-white sm:flex" data-id="element-361" data-mp="22ka2esmd2a4l">
            <SlidersHorizontalIcon className="h-4 w-4" data-id="element-362" data-mp="2cgdeaq70ln4q" />
          </span>
          <div className="min-w-0 flex-1" data-id="element-363" data-mp="2ch4x10fijqdl">
            <h1 className="truncate text-base font-bold text-slate-900" data-id="element-364" data-mp="2ghoygg74dg5j">Admin Configuration</h1>
            <p className="hidden text-xs text-slate-500 sm:block" data-id="element-365" data-mp="i2lwh3ej7wy7">
              Changes apply to the loan record for every user in this workspace.
            </p>
          </div>

          <button type="button" onClick={onReset} className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 px-3 py-2 text-sm font-semibold text-slate-500 transition-colors hover:bg-slate-50" data-id="element-366" data-mp="m5qvgvzelgi3">
            <RotateCcwIcon className="h-4 w-4" data-id="element-367" data-mp="4v4veegu4xol" />
            <span className="hidden sm:inline" data-id="element-368" data-mp="t9vneejyscx">Reset to default</span>
          </button>
        </div>
      </header>

      <div className="mx-auto max-w-7xl px-4 py-6 sm:px-6" data-id="element-369" data-mp="xjskym8ma97y">
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-[260px_minmax(0,1fr)]" data-id="element-370" data-mp="xh6bui62vlnm">
          <aside className="lg:sticky lg:top-24 lg:self-start" data-id="element-371" data-mp="skz54n5uy64m">
            <nav aria-label="Configuration areas" className="flex gap-2 lg:flex-col" data-id="element-372" data-mp="ohjc00szynwo">
              {TABS.map(t => <button key={t.key} type="button" onClick={() => setTab(t.key)} aria-current={tab === t.key ? 'page' : undefined} className={`flex flex-1 items-center gap-3 rounded-xl border px-3 py-3 text-left transition-colors lg:flex-none ${tab === t.key ? 'border-blue-200 bg-white text-blue-700 shadow-sm' : 'border-transparent text-slate-500 hover:bg-white hover:text-slate-800'}`} data-id="element-373" data-mp="jiznbsn0qy0m">
                  <span className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg ${tab === t.key ? 'bg-blue-600 text-white' : 'bg-slate-200 text-slate-500'}`} data-id="element-374" data-mp="r2z735tc1641">
                    {t.icon}
                  </span>
                  <span className="min-w-0" data-id="element-375" data-mp="15qbw54icltz6">
                    <span className="block truncate text-sm font-semibold" data-id="element-376" data-mp="1apwubcpp1yau">{t.label}</span>
                    <span className="hidden truncate text-xs text-slate-400 sm:block" data-id="element-377" data-mp="1arrf8r0ytqex">
                      {t.blurb}
                    </span>
                  </span>
                </button>)}
            </nav>

            <dl className="mt-4 hidden rounded-xl border border-slate-200 bg-white p-3 lg:block" data-id="element-378" data-mp="3mylv9cmbpkth">
              {stats.map(s => <div key={s.label} className="flex items-center justify-between border-b border-slate-100 py-1.5 last:border-0" data-id="element-379" data-mp="3ix0c59j74aau">
                  <dt className="flex items-center gap-1.5 text-xs text-slate-500" data-id="element-380" data-mp="3rz9kbsqzrrl2">
                    {s.icon}
                    {s.label}
                  </dt>
                  <dd className="text-xs font-bold tabular-nums text-slate-800" data-id="element-381" data-mp="2z9udlf6d0kuy">{s.value}</dd>
                </div>)}
            </dl>
          </aside>

          <main className="min-w-0 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm sm:p-6" data-id="element-382" data-mp="3e0jpykvk460e">
            {tab === 'sections' && <SectionsTab config={config} onChange={onChange} data-id="element-383" data-mp="2li5xz1wwje98" />}
            {tab === 'rules' && <RulesTab config={config} values={values} onChange={onChange} data-id="element-384" data-mp="2lftbcbg78fpu" />}
            {tab === 'layout' && <LayoutTab config={config} onChange={onChange} data-id="element-385" data-mp="2gh8jp0f0j4d0" />}
          </main>
        </div>
      </div>
    </div>;
}